import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const SCREEN_CATALOG = [
  {
    category: 'Front Desk & Patient Services',
    screens: [
      { code: 'REGISTRATION', name: 'Patient Registration', desc: 'Demographics, test booking, barcoding, and sample tagging' },
      { code: 'APPOINTMENTS', name: 'Appointments & Scheduling', desc: 'Online/walk-in slot booking and calendar management' },
      { code: 'QUEUE', name: 'Queue Desk & Tokens', desc: 'Waiting area queue token display & calling system' },
      { code: 'DISCOUNT_APPROVALS', name: 'Discount Approvals', desc: 'Managerial authorization workflow for invoice discounts' },
      { code: 'CANCELLATION_APPROVALS', name: 'Cancellation Approvals Inbox', desc: 'Multi-stage authorization workflow for bill cancellations' },
    ]
  },
  {
    category: 'Phlebotomy & Sample Logistics',
    screens: [
      { code: 'PHLEBOTOMY', name: 'Sample Collection (Phlebotomy)', desc: 'Phlebotomist barcode scanning, tube tagging, collection verification' },
      { code: 'SAMPLE_RECEIVE', name: 'Sample Receiving & Accessioning', desc: 'Central lab receiving, accession verification, rejection with reason' },
      { code: 'B2B_LOGISTICS', name: 'B2B Specimen Logistics', desc: 'Routing samples to external reference labs, courier dispatch & tracking' },
    ]
  },
  {
    category: 'Clinical Diagnostics & Result Reporting',
    screens: [
      { code: 'PATHOLOGY_RESULTS', name: 'Pathology Results Workbench', desc: 'Observation entry, reference ranges, critical delta alerts, formulas' },
      { code: 'RADIOLOGY_RESULTS', name: 'Radiology Reporting Studio', desc: 'X-Ray, Ultrasound, CT/MRI rich-text editor, Word template docx import' },
      { code: 'MICROBIOLOGY', name: 'Microbiology Culture Workbench', desc: 'Culture incubation, colony counts, organism identification & AST panels' },
      { code: 'REPORT_APPROVAL', name: 'Doctor Report Approval & Release', desc: 'Digital signature, multi-stage approval, revision hold, WhatsApp delivery' },
    ]
  },
  {
    category: 'Billing & Commercial Accounts',
    screens: [
      { code: 'SETTLEMENT', name: 'Receipt Settlement & Cash Counter', desc: 'Cash/UPI/card collections, daily ledger, refunds, counter closing' },
      { code: 'B2B_BILLING', name: 'B2B Corporate Billing', desc: 'Client contracts, monthly billing cycles, ledger reconciliation' },
    ]
  },
  {
    category: 'LIMS Masters & Reference Config',
    screens: [
      { code: 'MASTERS', name: 'LIMS Master Hub', desc: 'Central directory for tests, investigations, price lists, panels, and parameters' },
      { code: 'SIGNATURE_PROFILES', name: 'Doctor Signature Profiles', desc: 'Doctor digital signatures, degree credentials, profile mappings' },
      { code: 'CANCELLATION_POLICY', name: 'Cancellation Policy Master', desc: 'Configurable approval thresholds, refund caps, role-based policies' },
      { code: 'COLLECTION_CHARGE', name: 'Collection Charge Master', desc: 'Home collection distance slabs and phlebotomist service fee tariffs' },
    ]
  },
  {
    category: 'Security & Platform Governance',
    screens: [
      { code: 'EMPLOYEES', name: 'Employee & Staff Master', desc: 'Staff directory, departmental assignments, digital signature uploads' },
      { code: 'ROLES', name: 'Role-Based Access Control (RBAC)', desc: 'Screen and action level permissions per job role within organization' },
      { code: 'AUDIT_TRAIL', name: 'Compliance & Audit Trail', desc: 'Immutable activity logs, patient record edits, forensic tamper-proofing' },
      { code: 'REGISTRATION_CONFIG', name: 'Registration Configuration', desc: 'Custom fields, mandatory demographics, UHID & Lab No prefixes' },
      { code: 'NABL_CONFIG', name: 'Report & NABL Configuration', desc: 'NABL accreditation logos, report headers, disclaimers, QR codes' },
    ]
  }
];

const SaasPlansAndSubscriptions = () => {
  const [activeTab, setActiveTab] = useState('matrix'); // 'matrix' | 'catalogue'
  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [licenseProfile, setLicenseProfile] = useState(null);
  const [featureElements, setFeatureElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [screenSearch, setScreenSearch] = useState('');

  const navigate = useNavigate();

  // Fetch initial plans and tenants
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const [plansRes, tenantsRes] = await Promise.all([
          saasPlatformService.getLicensePlans(),
          saasPlatformService.getTenants()
        ]);
        
        const planList = plansRes?.data || plansRes || [];
        const tenantList = tenantsRes?.data || tenantsRes || [];
        setPlans(planList);
        setTenants(tenantList);

        if (tenantList.length > 0) {
          const defaultTenant = tenantList.find(t => t.tenantId === 1) || tenantList[0];
          setSelectedTenantId(defaultTenant.tenantId);
        }
      } catch (err) {
        console.error('Failed to load licensing plans or tenants', err);
        setError('Failed to load platform licensing catalogue or tenant accounts.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // Fetch tenant license profile & granular feature elements
  const loadTenantLicensing = useCallback(async (tenantId) => {
    if (!tenantId) return;
    setTenantLoading(true);
    setError(null);
    try {
      const [profileRes, elementsRes] = await Promise.all([
        saasPlatformService.getTenantLicenseProfile(tenantId),
        saasPlatformService.getTenantFeatureElements(tenantId)
      ]);

      setLicenseProfile(profileRes?.data || profileRes);
      setFeatureElements(elementsRes?.data || elementsRes || []);
    } catch (err) {
      console.error(`Failed to load licensing for tenant #${tenantId}`, err);
      setError(err?.response?.data?.message || `Unable to load licensing profile for Tenant #${tenantId}.`);
    } finally {
      setTenantLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTenantId) {
      loadTenantLicensing(selectedTenantId);
    }
  }, [selectedTenantId, loadTenantLicensing]);

  // One-click apply commercial tier
  const handleApplyPlan = async (planCode) => {
    if (!selectedTenantId) return;
    const confirmMsg = `Apply commercial plan '${planCode}' to Tenant #${selectedTenantId}?\nThis will adjust their licensed modules and quotas to match the commercial tier.`;
    if (!window.confirm(confirmMsg)) return;

    setActionLoading(true);
    setError(null);
    try {
      const res = await saasPlatformService.applyLicensePlan(selectedTenantId, planCode);
      setSuccess(`Plan '${planCode}' successfully applied to Tenant #${selectedTenantId}!`);
      setLicenseProfile(res?.data || res);
      await loadTenantLicensing(selectedTenantId);
    } catch (err) {
      console.error('Failed to apply plan', err);
      setError(err?.response?.data?.message || 'Failed to apply commercial plan.');
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle specific screen entitlement
  const handleToggleScreen = async (screenCode, currentLicensed) => {
    if (!selectedTenantId) return;
    setActionLoading(true);
    setError(null);
    try {
      const nextState = !currentLicensed;
      await saasPlatformService.updateTenantScreenEntitlement(selectedTenantId, screenCode, nextState);
      
      // Optimistic update of local state
      setLicenseProfile(prev => {
        if (!prev) return prev;
        const screens = prev.licensedScreens || [];
        const nextScreens = nextState 
          ? [...screens, screenCode]
          : screens.filter(s => s.toUpperCase() !== screenCode.toUpperCase());
        return {
          ...prev,
          planCode: 'CUSTOM',
          planName: 'Custom Enterprise Configuration',
          licensedScreens: nextScreens
        };
      });

      setSuccess(`Screen '${screenCode}' set to ${nextState ? 'LICENSED' : 'UNLICENSED'} for Tenant #${selectedTenantId}.`);
      
      // Invalidate server cache and reload elements
      await saasPlatformService.evictTenantLicenseCache(selectedTenantId);
      const elementsRes = await saasPlatformService.getTenantFeatureElements(selectedTenantId);
      setFeatureElements(elementsRes?.data || elementsRes || []);
    } catch (err) {
      console.error('Failed to toggle screen', err);
      setError(err?.response?.data?.message || `Failed to update screen entitlement for ${screenCode}.`);
      await loadTenantLicensing(selectedTenantId);
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle granular feature element
  const handleToggleElement = async (elementKey, currentEnabled) => {
    if (!selectedTenantId) return;
    setActionLoading(true);
    setError(null);
    try {
      const nextState = !currentEnabled;
      await saasPlatformService.updateTenantElementOverride(selectedTenantId, elementKey, nextState);

      // Optimistic update of elements
      setFeatureElements(prev => prev.map(el => 
        el.elementKey === elementKey ? { ...el, isEnabled: nextState } : el
      ));

      // Also update profile element list
      setLicenseProfile(prev => {
        if (!prev) return prev;
        const elements = prev.licensedElements || [];
        const nextElements = nextState
          ? [...elements, elementKey]
          : elements.filter(e => e.toUpperCase() !== elementKey.toUpperCase());
        return { ...prev, licensedElements: nextElements };
      });

      setSuccess(`Granular element '${elementKey}' ${nextState ? 'ENABLED' : 'DISABLED'}.`);
      await saasPlatformService.evictTenantLicenseCache(selectedTenantId);
    } catch (err) {
      console.error('Failed to toggle element override', err);
      setError(err?.response?.data?.message || `Failed to update element ${elementKey}.`);
      await loadTenantLicensing(selectedTenantId);
    } finally {
      setActionLoading(false);
    }
  };

  // Cache purge
  const handleEvictCache = async () => {
    if (!selectedTenantId) return;
    try {
      await saasPlatformService.evictTenantLicenseCache(selectedTenantId);
      setSuccess(`Licensing cache purged for Tenant #${selectedTenantId}. Fresh configuration is live.`);
      await loadTenantLicensing(selectedTenantId);
    } catch (err) {
      setError('Failed to purge cache.');
    }
  };

  const currentTenantObj = tenants.find(t => t.tenantId === Number(selectedTenantId));
  const isScreenInLicense = (screenCode) => {
    if (!licenseProfile?.licensedScreens) return false;
    return licenseProfile.licensedScreens.some(s => s.toUpperCase() === screenCode.toUpperCase());
  };

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🛡️</span> Enterprise Super Admin &amp; Dynamic Module Licensing
          </h1>
          <p>Commercial Tiering, Granular Screen Entitlements &amp; Feature Element Control Engine</p>
        </div>
        <div className="saas-header-actions">
          <button 
            className={`saas-btn ${activeTab === 'matrix' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
            onClick={() => setActiveTab('matrix')}
          >
            📋 Client Entitlement Matrix
          </button>
          <button 
            className={`saas-btn ${activeTab === 'catalogue' ? 'saas-btn-primary' : 'saas-btn-secondary'}`}
            onClick={() => setActiveTab('catalogue')}
          >
            💳 Standard Pricing Tiers
          </button>
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/tenants')}>
            ← Back to Tenants
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box" style={{ marginBottom: '1.25rem' }}>
          <div><strong>Licensing Alert:</strong> {error}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="saas-success-box" style={{ marginBottom: '1.25rem' }}>
          <div><strong>Success:</strong> {success}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* TAB 1: PRICING TIERS CATALOGUE */}
      {activeTab === 'catalogue' && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1.2rem', margin: 0, color: '#0f172a' }}>
              Standard Commercial License Packages ({plans.length} Tiers)
            </h3>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
              Universal Codebase • Instant Feature Provisioning
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem' }}>
            {plans.map(p => {
              const isStarter = p.planCode === 'STARTER_2L';
              const isDiag = p.planCode === 'DIAGNOSTIC_5L';
              const isEnt = p.planCode === 'ENTERPRISE_10L';
              const borderColor = isEnt ? '#7c3aed' : isDiag ? '#0284c7' : isStarter ? '#10b981' : '#f59e0b';
              const badgeColor = isEnt ? '#ede9fe' : isDiag ? '#e0f2fe' : isStarter ? '#d1fae5' : '#fef3c7';
              const badgeTextColor = isEnt ? '#6d28d9' : isDiag ? '#0369a1' : isStarter ? '#047857' : '#b45309';

              return (
                <div key={p.planCode} className="saas-card" style={{ borderTop: `5px solid ${borderColor}`, position: 'relative' }}>
                  <div className="saas-card-header" style={{ paddingBottom: '0.5rem' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>{p.planName}</h4>
                      <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#64748b' }}>{p.planCode}</span>
                    </div>
                    <span style={{ background: badgeColor, color: badgeTextColor, padding: '0.35rem 0.75rem', borderRadius: '999px', fontWeight: 700, fontSize: '0.9rem' }}>
                      ₹{Number(p.priceInInr).toLocaleString('en-IN')}
                    </span>
                  </div>

                  <div className="saas-card-body" style={{ fontSize: '0.8125rem' }}>
                    <p style={{ color: '#475569', margin: '0 0 1rem 0', minHeight: '36px' }}>{p.description}</p>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', background: '#f8fafc', padding: '0.85rem', borderRadius: '6px', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Authorized Branches / Centres:</span>
                        <strong>{p.maxCentres || 'Unlimited'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Staff User Accounts:</span>
                        <strong>{p.maxUsers || 'Unlimited'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Monthly Patient Capacity:</span>
                        <strong>{Number(p.maxPatientsPerMonth).toLocaleString('en-IN')} / mo</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b' }}>Included Screens:</span>
                        <strong style={{ color: borderColor }}>{p.includedScreens?.length || 0} Modules</strong>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1.25rem' }}>
                      <strong style={{ display: 'block', marginBottom: '0.4rem', color: '#334155' }}>Core Included Modules:</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {(p.includedScreens || []).slice(0, 8).map(s => (
                          <span key={s} style={{ background: '#f1f5f9', color: '#475569', padding: '2px 6px', borderRadius: '4px', fontSize: '0.72rem', fontFamily: 'monospace' }}>
                            {s}
                          </span>
                        ))}
                        {(p.includedScreens || []).length > 8 && (
                          <span style={{ color: '#64748b', fontSize: '0.72rem', alignSelf: 'center' }}>
                            +{(p.includedScreens.length - 8)} more...
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      className="saas-btn saas-btn-primary"
                      style={{ width: '100%', justifyContent: 'center', background: borderColor, borderColor }}
                      onClick={() => {
                        setActiveTab('matrix');
                        handleApplyPlan(p.planCode);
                      }}
                      disabled={actionLoading}
                    >
                      🚀 Apply Tier to Selected Client
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 2: TENANT ENTITLEMENT MATRIX */}
      {activeTab === 'matrix' && (
        <div>
          {/* Tenant Selector & Subscription Summary Bar */}
          <div className="saas-card" style={{ marginBottom: '1.5rem' }}>
            <div className="saas-card-body" style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: '320px' }}>
                  <label style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                    🏢 Select Hospital / Client:
                  </label>
                  <select
                    className="saas-select"
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(Number(e.target.value))}
                    style={{ flex: 1, fontWeight: 600, padding: '0.6rem 0.85rem' }}
                  >
                    {tenants.map(t => (
                      <option key={t.tenantId} value={t.tenantId}>
                        #{t.tenantId} — {t.tenantCode} ({t.organizationName}) [{t.status}]
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <button 
                    className="saas-btn saas-btn-secondary" 
                    onClick={handleEvictCache}
                    title="Evicts in-memory license cache to force immediate token & menu refresh"
                  >
                    🔄 Purge &amp; Sync Cache
                  </button>
                </div>
              </div>

              {/* Active Plan Overview */}
              {licenseProfile && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Active Commercial Plan</span>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0284c7', marginTop: '0.2rem' }}>
                      {licenseProfile.planCode}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#475569' }}>{licenseProfile.planName}</span>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>License Status</span>
                    <div style={{ marginTop: '0.25rem' }}>
                      <span className={`saas-badge ${licenseProfile.licenseStatus === 'ACTIVE' ? 'saas-badge-active' : 'saas-badge-warning'}`}>
                        ● {licenseProfile.licenseStatus}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginTop: '0.2rem' }}>
                      Quota: {licenseProfile.maxCentres} Centres / {licenseProfile.maxUsers} Users
                    </span>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Licensed Modules</span>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#10b981', marginTop: '0.2rem' }}>
                      {licenseProfile.licensedScreens?.length || 0} / 23 Screens
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {licenseProfile.licensedElements?.length || 0} Feature Element Overrides
                    </span>
                  </div>

                  <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Fast Plan Switcher</span>
                    <div style={{ display: 'flex', gap: '0.35rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                      <button 
                        className="saas-btn" 
                        style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0' }}
                        onClick={() => handleApplyPlan('STARTER_2L')}
                        disabled={actionLoading}
                      >
                        Starter (₹2L)
                      </button>
                      <button 
                        className="saas-btn" 
                        style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem', background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}
                        onClick={() => handleApplyPlan('DIAGNOSTIC_5L')}
                        disabled={actionLoading}
                      >
                        Diagnostic (₹5L)
                      </button>
                      <button 
                        className="saas-btn" 
                        style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem', background: '#faf5ff', color: '#6d28d9', border: '1px solid #ddd6fe' }}
                        onClick={() => handleApplyPlan('ENTERPRISE_10L')}
                        disabled={actionLoading}
                      >
                        Enterprise (₹10L)
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Screen Entitlement Matrix */}
          <div className="saas-card" style={{ marginBottom: '1.5rem' }}>
            <div className="saas-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Granular Screen &amp; Module Entitlements</h3>
                <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                  Enable or disable entire clinical screens for #{selectedTenantId} ({currentTenantObj?.organizationName}). Toggling will automatically switch tier to CUSTOM.
                </span>
              </div>
              <div>
                <input
                  type="text"
                  placeholder="🔍 Filter screens..."
                  value={screenSearch}
                  onChange={(e) => setScreenSearch(e.target.value)}
                  style={{ padding: '0.45rem 0.85rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem' }}
                />
              </div>
            </div>

            <div className="saas-card-body">
              {tenantLoading ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>⏳ Loading client licensing profile...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
                  {SCREEN_CATALOG.map(group => {
                    const filteredScreens = group.screens.filter(s => 
                      s.name.toLowerCase().includes(screenSearch.toLowerCase()) ||
                      s.code.toLowerCase().includes(screenSearch.toLowerCase()) ||
                      s.desc.toLowerCase().includes(screenSearch.toLowerCase())
                    );

                    if (filteredScreens.length === 0) return null;

                    return (
                      <div key={group.category} style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
                          <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            📂 {group.category}
                          </h4>
                          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                            {filteredScreens.filter(s => isScreenInLicense(s.code)).length} of {filteredScreens.length} enabled
                          </span>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.85rem' }}>
                          {filteredScreens.map(scr => {
                            const isLicensed = isScreenInLicense(scr.code);
                            return (
                              <div 
                                key={scr.code}
                                style={{
                                  background: isLicensed ? '#ffffff' : '#f1f5f9',
                                  border: `1.5px solid ${isLicensed ? '#a7f3d0' : '#e2e8f0'}`,
                                  borderRadius: '8px',
                                  padding: '0.85rem',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  transition: 'all 0.15s ease'
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: '0.875rem', color: isLicensed ? '#0f172a' : '#64748b' }}>
                                      {scr.name}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#64748b' }}>
                                      {scr.code}
                                    </div>
                                  </div>

                                  <button
                                    onClick={() => handleToggleScreen(scr.code, isLicensed)}
                                    disabled={actionLoading}
                                    style={{
                                      cursor: 'pointer',
                                      padding: '0.35rem 0.75rem',
                                      borderRadius: '20px',
                                      border: 'none',
                                      fontWeight: 700,
                                      fontSize: '0.75rem',
                                      background: isLicensed ? '#10b981' : '#cbd5e1',
                                      color: isLicensed ? '#ffffff' : '#475569',
                                      transition: 'background 0.2s ease'
                                    }}
                                  >
                                    {isLicensed ? '✓ LICENSED' : '✕ LOCKED'}
                                  </button>
                                </div>

                                <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
                                  {scr.desc}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Granular Feature Elements Matrix */}
          <div className="saas-card">
            <div className="saas-card-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Granular UI Feature Overrides ({featureElements.length} Elements)</h3>
                <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                  Turn high-value buttons, AI assistants, and tabs on or off for #{selectedTenantId} without touching code.
                </span>
              </div>
            </div>

            <div className="saas-card-body">
              {featureElements.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                  No granular feature elements found for this tenant.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.85rem' }}>
                  {featureElements.map(el => {
                    const isEnabled = Boolean(el.isEnabled);
                    return (
                      <div 
                        key={el.elementKey}
                        style={{
                          background: isEnabled ? '#ffffff' : '#f8fafc',
                          border: `1.5px solid ${isEnabled ? '#bfdbfe' : '#e2e8f0'}`,
                          borderRadius: '8px',
                          padding: '0.85rem',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.4rem' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: isEnabled ? '#0f172a' : '#64748b' }}>
                              {el.elementName}
                            </div>
                            <div style={{ fontSize: '0.7rem', fontFamily: 'monospace', color: '#0284c7' }}>
                              {el.elementKey}
                            </div>
                            <span style={{ fontSize: '0.68rem', background: '#f1f5f9', color: '#475569', padding: '1px 5px', borderRadius: '4px' }}>
                              {el.screenCode} • {el.elementType}
                            </span>
                          </div>

                          <button
                            onClick={() => handleToggleElement(el.elementKey, isEnabled)}
                            disabled={actionLoading}
                            style={{
                              cursor: 'pointer',
                              padding: '0.3rem 0.65rem',
                              borderRadius: '20px',
                              border: 'none',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              background: isEnabled ? '#0284c7' : '#cbd5e1',
                              color: isEnabled ? '#ffffff' : '#475569'
                            }}
                          >
                            {isEnabled ? 'ACTIVE' : 'OFF'}
                          </button>
                        </div>

                        <p style={{ fontSize: '0.74rem', color: '#64748b', margin: 0 }}>
                          {el.description || 'Feature element switch'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasPlansAndSubscriptions;
